import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useI18n } from '../context/I18nContext';
import { MenuList } from '../jsx/layouts/nav/Menu';
import { findMenuTrail } from '../jsx/layouts/nav/menu-trail';
import { getRoleRoutes, resolveAppRole } from '../jsx/routes/routeRegistry';
import { useBadgeCardDownload } from '../shared/hooks/useBadgeCardDownload';
import { useStartWelcomeTour } from '../shared/onboarding/useWelcomeTour';
import { findPageTour, requestPageTour } from '../shared/onboarding/page-tours';
import { SUPPORT_FAQ, buildManualSectionUrl, findManualSection } from '../shared/support/page-help';
import './support-page-help.css';

type SupportPageHelpProps = {
    manualUrl: string;
    /** Bir baglantiya tiklaninca paneli kapatmak icin. */
    onNavigate: () => void;
};

/**
 * "Yaka kartimi nereden indiririm?" → karti dogrudan indirir (profil menusu ve
 * anasayfadaki kartla ayni islev). Indirme bitene kadar panel acik kalir.
 */
const BadgeDownloadFaq = ({ question }: { question: string }) => {
    const { t } = useI18n();
    const { isBadgeDownloading, handleBadgeDownload, badgeCanvas } = useBadgeCardDownload();

    return (
        <>
            <button type="button" onClick={handleBadgeDownload} disabled={isBadgeDownloading}>
                <span>{isBadgeDownloading ? t('support.faq.badgeDownloading') : question}</span>
                {isBadgeDownloading ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                ) : (
                    <i className="fa-solid fa-download" aria-hidden="true" />
                )}
            </button>
            {badgeCanvas}
        </>
    );
};

const isSignedIn = () => {
    try {
        return Boolean(window.localStorage.getItem('userDetails'));
    } catch {
        return false;
    }
};

/**
 * Destek panelinin ust kismi (Yonlendirme Faz 3.4): bulunulan sayfanin
 * kilavuz bolumune baglanti + kullanicinin rolune acik sayfalara giden sik
 * sorulan sorular. Oturum yokken (giris sayfasi vb.) gosterilmez.
 * Faz 4.2: karsilama turunu ve (varsa) bu sayfanin turunu baslatma dugmeleri de buradadir.
 */
const SupportPageHelp = ({ manualUrl, onNavigate }: SupportPageHelpProps) => {
    const { t } = useI18n();
    const { pathname, search } = useLocation();
    const signedIn = isSignedIn();
    const startWelcomeTour = useStartWelcomeTour();

    const faqItems = useMemo(() => {
        if (!signedIn) return [];
        const allowedPaths = new Set(getRoleRoutes(resolveAppRole()).map((route) => route.path));
        return SUPPORT_FAQ.filter((item) => allowedPaths.has(item.routePath));
    }, [signedIn]);

    if (!signedIn) return null;

    const sectionId = manualUrl ? findManualSection(pathname) : null;
    const trail = findMenuTrail(MenuList, pathname, search);
    const pageTitle = trail ? t(trail[trail.length - 1]) : '';
    const hasPageTour = Boolean(findPageTour(pathname));

    return (
        <div className="support-page-help">
            {sectionId ? (
                <a
                    className="support-page-help-manual"
                    href={buildManualSectionUrl(manualUrl, sectionId)}
                    target="_blank"
                    rel="noreferrer"
                    onClick={onNavigate}
                >
                    <i className="fa-regular fa-circle-question" aria-hidden="true" />
                    <span>
                        <span className="support-page-help-label">{t('support.pageHelp.title')}</span>
                        <span className="support-page-help-text">
                            {pageTitle
                                ? t('support.pageHelp.open').replace('{page}', pageTitle)
                                : t('support.pageHelp.openGeneric')}
                        </span>
                    </span>
                    <i className="fa-solid fa-arrow-up-right-from-square support-page-help-external" aria-hidden="true" />
                </a>
            ) : null}

            <button
                type="button"
                className="support-page-help-manual support-page-help-tour"
                onClick={() => {
                    onNavigate();
                    startWelcomeTour();
                }}
            >
                <i className="fa-solid fa-route" aria-hidden="true" />
                <span className="support-page-help-text">{t('tour.start')}</span>
            </button>

            {hasPageTour ? (
                <button
                    type="button"
                    className="support-page-help-manual support-page-help-tour"
                    onClick={() => {
                        onNavigate();
                        requestPageTour();
                    }}
                >
                    <i className="fa-solid fa-signs-post" aria-hidden="true" />
                    <span className="support-page-help-text">{t('pageTour.replay')}</span>
                </button>
            ) : null}

            {faqItems.length > 0 ? (
                <div className="support-page-help-faq">
                    <div className="support-page-help-label">{t('support.faq.title')}</div>
                    <ul>
                        {faqItems.map((item) => (
                            <li key={item.questionKey}>
                                {item.action === 'downloadBadge' ? (
                                    <BadgeDownloadFaq question={t(item.questionKey)} />
                                ) : (
                                    <Link to={item.to ?? '/home'} onClick={onNavigate}>
                                        <span>{t(item.questionKey)}</span>
                                        <i className="fa-solid fa-chevron-right" aria-hidden="true" />
                                    </Link>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            ) : null}
        </div>
    );
};

export default SupportPageHelp;
